export const PLATFORM_ROLES = Object.freeze({
  SUPERADMIN: 'saas_superadmin',
  SUPPORT: 'platform_support',
  AUDITOR: 'platform_auditor',
});

export const INSTITUTION_ROLES = Object.freeze({
  ADMIN: 'institution_admin',
  SECURITY_ADMIN: 'security_admin_delegate',
  EXECUTIVE: 'executive_oversight',
  AUDITOR: 'compliance_auditor',
});

const normalizeRole = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_');

const normalizedRoles = (values) => {
  const list = Array.isArray(values) ? values : [values];
  return [...new Set(list.map(normalizeRole).filter(Boolean))];
};

export const unwrapApiData = (response) => {
  if (response == null) return response;
  if (response.status === false) return null;
  if (response.status === true && Object.prototype.hasOwnProperty.call(response, 'data')) {
    return response.data;
  }
  return response;
};

export const apiErrorMessage = (response, fallback = 'The request could not be completed.') => {
  const data = response?.data;
  const message = response?.message || response?.detail || data?.detail || data?.message;
  if (Array.isArray(message)) return message.map((item) => item?.msg || item).join(' ');
  return message ? String(message) : fallback;
};

export const extractAuthenticationResult = (response) => {
  const payload = unwrapApiData(response);
  if (!payload || typeof payload !== 'object') return null;
  if (payload.requires_institution_selection) {
    return {
      type: 'institution_selection',
      selectionToken: payload.selection_token,
      institutions: Array.isArray(payload.institutions) ? payload.institutions : [],
    };
  }
  if (payload.access_token && payload.user) {
    return {
      type: 'authenticated',
      accessToken: payload.access_token,
      user: payload.user,
      tokenType: payload.token_type || 'bearer',
    };
  }
  return null;
};

export const getSaasContext = (user) => user?.saas_context || {};

export const getPlatformRoles = (user) => normalizedRoles([
  ...(getSaasContext(user).platform_roles || []),
  ...(user?.platform_roles || []),
  ...(Array.isArray(user?.role) ? user.role : [user?.role]),
]);

export const getInstitutionRoles = (user) => {
  const context = getSaasContext(user);
  const hasSelectedMembership = Boolean(
    user?._institution_roles_authoritative === true
    || context.institution_roles_authoritative === true
    || user?.membership_id
    || context.membership_id
  );
  return normalizedRoles(hasSelectedMembership
    ? (context.institution_roles || [])
    : [
      ...(context.institution_roles || []),
      ...(user?.institution_roles || []),
    ]);
};

export const isPlatformSuperadmin = (user) => {
  const roles = getPlatformRoles(user);
  return roles.includes(PLATFORM_ROLES.SUPERADMIN)
    || roles.includes('superadmin')
    || roles.includes('super_admin');
};

export const isPlatformSupport = (user) => getPlatformRoles(user).includes(PLATFORM_ROLES.SUPPORT);

export const canManageInstitutionAccess = (user) => {
  if (isPlatformSuperadmin(user)) return true;
  const roles = getInstitutionRoles(user);
  return roles.includes(INSTITUTION_ROLES.ADMIN)
    || roles.includes(INSTITUTION_ROLES.SECURITY_ADMIN);
};

export const canManageInstitutionUsers = (user) => {
  if (isPlatformSuperadmin(user)) return true;
  return getInstitutionRoles(user).includes(INSTITUTION_ROLES.ADMIN);
};

export const activeInstitutionId = (user) => String(
  getSaasContext(user).institution_id || user?.institution_id || ''
).trim();

export const institutionHeaderDisplayName = (user, institution) => {
  const institutionId = activeInstitutionId(user);
  if (!institutionId || isPlatformSuperadmin(user)) return '';
  if (String(institution?.id || '').trim() !== institutionId) return '';
  return String(institution?.display_name || institution?.slug || '').trim();
};

export const persistAuthenticationResult = (result, institution = null) => {
  if (!result?.accessToken || !result?.user) return false;
  localStorage.setItem('access_token', result.accessToken);
  localStorage.setItem('user', JSON.stringify(result.user));
  if (institution) {
    localStorage.setItem('active_institution', JSON.stringify(institution));
  } else {
    const institutionId = activeInstitutionId(result.user);
    if (institutionId) {
      localStorage.setItem('active_institution', JSON.stringify({
        id: institutionId,
        display_name: result.user?.institution_name || institutionId,
      }));
    } else {
      localStorage.removeItem('active_institution');
    }
  }
  window.dispatchEvent(new CustomEvent('saas-session-changed', {
    detail: { user: result.user, institution },
  }));
  return true;
};

export const readStoredUser = () => {
  try {
    const value = localStorage.getItem('user');
    return value ? JSON.parse(value) : null;
  } catch (_) {
    return null;
  }
};

export const readStoredInstitution = () => {
  try {
    const value = localStorage.getItem('active_institution');
    return value ? JSON.parse(value) : null;
  } catch (_) {
    return null;
  }
};
